<template>
    <template v-for="(popover, i) in currentLayer" :key="popover.id">
        <component
            v-if="popover.kind === 'mag-dialog'"
            :is="popover.component"
        />
        <component
            v-else-if="popover.kind === 'legacy-dialog'"
            :is="popover.component"
            v-bind="popover.props"
            v-on="popover.events"
        />
        <MagTransition
            v-else-if="popover.kind === 'context-menu'"
            :name="popover.dropdown ? 'mag-menu-vertical' : 'mag-menu'"
            appear
            :duration="200"
            @after-leave="removePopover(popover.id)"
        >
            <dialog
                v-if="!popover.closing"
                class="mag-menu _popup _shadow"
                closedby="closerequest"
                :popover="popover.isChild ? 'manual' : undefined"
                @close="closePopover(i)"
                @vue:mounted="
                    (e: VNode) => {
                        if (popover.isChild) {
                            e.el?.showPopover();
                        } else {
                            e.el?.showModal();
                        }
                    }
                "
            >
                <component
                    :is="popover.component"
                    @close="(viaAction) => closePopover(i, viaAction)"
                />
                <MagPopoverLayer v-if="!popover.isChild" />
            </dialog>
        </MagTransition>
    </template>
</template>

<script setup lang="ts">
import {
    PopoverLayer,
    popoverLayers,
    removePopover,
} from "@/components/layout/popover-layer";
import { onUnmounted, type VNode } from "vue";

const placement = popoverLayers.findLastIndex((l) => !l.closing) + 1;
const layer = new PopoverLayer(placement);
const currentLayer = layer.items;
for (const afterLayer of popoverLayers.slice(placement)) {
    afterLayer.id += 1;
}
popoverLayers.splice(placement, 0, layer);

function closePopover(idx: number, viaAction: boolean = true) {
    const popover = currentLayer[idx];
    if (popover.kind === "context-menu") {
        // Ensure context menus close in the correct order
        const inner = popoverLayers.at(layer.id + 2);
        if (inner != null && !inner.closing) {
            return;
        }

        // Invariant: Child context menus (those spawned by nested menus)
        // do not have their own popover layer
        if (!popover.isChild) {
            popoverLayers[layer.id + 1].closing = true;
        }

        popover.closing = true;
    }
}

onUnmounted(() => {
    popoverLayers.splice(layer.id);
});
</script>

<style lang="scss" scoped>
.mag-menu-enter-active,
.mag-menu-leave-active {
    transition:
        opacity 0.15s cubic-bezier(0.22, 1, 0.36, 1),
        transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.mag-menu-enter-from {
    opacity: 0;
    transform: scale(0.6);
}

.mag-menu-leave-to {
    opacity: 0;
    transform: scaleY(0.4);
}

.mag-menu-vertical-enter-active,
.mag-menu-vertical-leave-active {
    transition:
        opacity 0.15s cubic-bezier(0.22, 1, 0.36, 1),
        transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.mag-menu-vertical-enter-from {
    opacity: 0;
    transform: scaleY(0.6);
}

.mag-menu-vertical-leave-to {
    opacity: 0;
    transform: scaleY(0.4);
}

.mag-menu {
    &::backdrop {
        display: none;
    }

    pointer-events: auto;
    display: inline-flex;
    flex-direction: column;
    position: fixed;
    background: var(--popup);
    border-radius: var(--radius);
    border: 1px solid var(--accentedBg);
    contain: content;
    inset: auto;
    padding: 0;
    max-width: 100%;
    box-sizing: border-box;
}
</style>
